import type { TEntityChoice } from '@verifime/components';
import {
  buildEntityPlan,
  type TEntityPlan,
  type TEntityRequestType,
  type TPlanChosen,
  type TPlanIdentity,
  type TPlanRequest,
} from './entityPlan';
import type { TSharedIdentityIds } from './hooks/useSharedIdentityIds';

/** One query's state, as far as the confirm screen cares. */
export type TConfirmQueryInput = {
  /**
   * True once this mount has completed a fetch (success or failure); false while only a cache
   * answers.
   */
  isFetchedAfterMount: boolean;
  hasError: boolean;
  data: unknown;
};

export type TConfirmEntityPlanInputs = {
  metadata: {
    isLoading: boolean;
    hasError: boolean;
    organisationId?: string;
    organisationName?: string;
    logoUrl?: string;
  };
  /** The choice to show the plan for; absent to show the client's confirmed choice. */
  choice?: TEntityChoice;
  /** What the customer chose on the choose screen: a wallet identity (`{ identityId }`) or a new one (`{}`). */
  chosen?: TPlanChosen;
  /** The open entity request being worked on, when opened from a request (the request path). */
  entityRequestType?: TEntityRequestType;
  /**
   * Which of the customer's identities are already shared with this client
   * (`useSharedIdentityIds`). Absent when the caller does not read it: then nothing is shared.
   */
  sharedIdentities?: TSharedIdentityIds;
  /** The organisation name the partner declared for a new entity, as given. */
  declaredOrganisationName?: string;
  /** Closes the change link once the entity draft exists (#616); see `buildEntityPlan`. */
  closeChangeOnEntityDraft?: boolean;
  /** `GET /v1/permission/request/me`: expected to be an array of request summaries. */
  requests: TConfirmQueryInput;
  /** `GET /v1/customer/me`: expected to be `{ customerList: [...] }`. */
  identities: TConfirmQueryInput;
  /** The last loaded state, kept when a background refetch fails. */
  previous?: TConfirmEntityPlanLoaded;
};

export type TConfirmEntityPlanErrorReason =
  | 'metadata-failed'
  | 'no-signup-client'
  | 'requests-failed'
  | 'requests-invalid'
  | 'identities-failed'
  | 'identities-invalid'
  | 'shared-identities-failed'
  | 'no-request-for-client';

export type TConfirmEntityPlanLoaded = {
  status: 'loaded';
  plan: TEntityPlan;
  /** The client the plan is for: who the share is granted to. */
  organisationId: string;
  organisationName?: string;
  logoUrl?: string;
};

export type TConfirmEntityPlanState =
  | { status: 'loading' }
  | { status: 'error'; reason: TConfirmEntityPlanErrorReason }
  | TConfirmEntityPlanLoaded;

const error = (reason: TConfirmEntityPlanErrorReason) => ({ status: 'error', reason }) as const;

const requestListOf = (data: unknown): TPlanRequest[] | undefined =>
  Array.isArray(data) ? (data as TPlanRequest[]) : undefined;

const identityListOf = (data: unknown): TPlanIdentity[] | undefined => {
  const list = (data as { customerList?: unknown } | undefined)?.customerList;
  return Array.isArray(list) ? (list as TPlanIdentity[]) : undefined;
};

/** Still waiting for a read that has not run since this mount. */
const isWaiting = ({
  metadata,
  requests,
  identities,
  sharedIdentities,
}: TConfirmEntityPlanInputs): boolean =>
  metadata.isLoading ||
  !requests.isFetchedAfterMount ||
  !identities.isFetchedAfterMount ||
  sharedIdentities?.status === 'loading';

/** The first read that failed or came back malformed, if any. */
function queryFailureOf(
  { requests, identities, sharedIdentities }: TConfirmEntityPlanInputs,
  requestList: TPlanRequest[] | undefined,
  identityList: TPlanIdentity[] | undefined,
): TConfirmEntityPlanErrorReason | undefined {
  if (requests.hasError) {
    return 'requests-failed';
  }
  if (identities.hasError) {
    return 'identities-failed';
  }
  if (!requestList) {
    return 'requests-invalid';
  }
  if (!identityList) {
    return 'identities-invalid';
  }
  return sharedIdentities?.status === 'error' ? 'shared-identities-failed' : undefined;
}

/**
 * The confirm screen's state from its reads.
 *
 * - Loading until the metadata has loaded and every query has fetched since this mount: a cached
 *   answer (or cached error) from an earlier screen must not decide what is shown.
 * - A failed or malformed query, or shared identities that could not be read, is the error state,
 *   unless a plan was already loaded on this mount: a failed background refetch keeps the plan on
 *   screen.
 * - A metadata failure, a tracking reference with no sign-up client, or (with no choice given) no
 *   ask-first permission request for that client is always the error state: none of them is a
 *   plausible plan.
 */
export function deriveConfirmEntityPlanState(
  inputs: TConfirmEntityPlanInputs,
): TConfirmEntityPlanState {
  const { metadata, choice, chosen, entityRequestType, requests, identities, previous } = inputs;
  if (metadata.hasError) {
    return error('metadata-failed');
  }
  if (isWaiting(inputs)) {
    return { status: 'loading' };
  }

  const requestList = requestListOf(requests.data);
  const identityList = identityListOf(identities.data);
  const queryFailure = queryFailureOf(inputs, requestList, identityList);
  if (queryFailure) {
    return previous ?? error(queryFailure);
  }

  if (!metadata.organisationId) {
    return error('no-signup-client');
  }

  const plan = buildEntityPlan({
    organisationId: metadata.organisationId,
    choice,
    chosen,
    entityRequestType,
    requests: requestList as TPlanRequest[],
    identities: identityList as TPlanIdentity[],
    sharedIdentityIds: inputs.sharedIdentities?.ids,
    declaredOrganisationName: inputs.declaredOrganisationName,
    closeChangeOnEntityDraft: inputs.closeChangeOnEntityDraft,
  });
  if (!plan) {
    return error('no-request-for-client');
  }

  return {
    status: 'loaded',
    plan,
    organisationId: metadata.organisationId,
    organisationName: metadata.organisationName,
    logoUrl: metadata.logoUrl,
  };
}
